import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * Onboarding — invite acceptance for newly invited recruiters / client admins.
 * URL: /onboarding?token=…
 * - Validates the token, pulls the invited email + company.
 * - Email field is pre-filled & disabled.
 * - User sets a new password (8+ chars, confirm).
 * - On success → log them in automatically and route to dashboard.
 */
const Onboarding = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [validating, setValidating] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const [form, setForm] = useState({ firstName: '', lastName: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!token) { setValidating(false); setValidationError('Missing invitation token'); return; }
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invite/validate/${token}`);
        if (r.data?.success) {
          setInvitation(r.data.data);
          setForm(f => ({
            ...f,
            firstName: r.data.data.first_name || r.data.data.firstName || '',
            lastName: r.data.data.last_name || r.data.data.lastName || '',
          }));
        } else {
          setValidationError(r.data?.message || 'Invalid invitation');
        }
      } catch (e) {
        setValidationError(e?.response?.data?.message || 'Invalid or expired invitation link');
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name required';
    if (!form.lastName.trim()) e.lastName = 'Last name required';
    if (!form.password) e.password = 'Password required';
    else if (form.password.length < 8) e.password = 'At least 8 characters';
    if (form.confirmPassword !== form.password) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/invite/complete-recruiter-registration`,
        {
          token,
          email: invitation.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
        }
      );
      if (r.data?.success) {
        // Auto-login
        const { error } = await signIn(invitation.email, form.password);
        if (error) {
          // Fall back to login page
          navigate('/login', { state: { message: 'Account created — please sign in.', email: invitation.email } });
        } else {
          // signIn will trigger the routing inside AuthContext / Login
          navigate('/dashboard', { replace: true });
        }
      } else {
        setSubmitError(r.data?.message || 'Failed to create account');
      }
    } catch (e) {
      setSubmitError(e?.response?.data?.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────── Render ────────────────────────
  if (validating) {
    return (
      <AuthLayout>
        <div className="flex items-center gap-2 text-gray-500" data-testid="onboarding-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Verifying your invitation…
        </div>
      </AuthLayout>
    );
  }

  if (validationError || !invitation) {
    return (
      <AuthLayout>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Invitation invalid</h1>
        <p className="text-sm text-gray-500 mb-5">{validationError || 'This link is no longer valid.'}</p>
        <Link to="/login" className="text-sm text-blue-500 hover:text-blue-600 font-semibold">
          Go to login →
        </Link>
      </AuthLayout>
    );
  }

  const companyName = invitation.organization_name || invitation.company_name || invitation.organization?.name || '';

  return (
    <AuthLayout
      heroImage="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?auto=format&fit=crop&w=1600&q=80"
      heroAlt="Welcome aboard"
    >
      <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight" data-testid="onboarding-title">
        <span className="text-blue-500">Welcome</span> aboard.
      </h1>
      <p className="text-sm text-gray-500 mt-3 mb-7">
        You&apos;re joining <span className="font-semibold text-gray-800">{companyName || 'JobastiIQ'}</span> — let&apos;s finish setting up your account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {submitError && (
          <Alert variant="destructive">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        {/* Email (read-only) */}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm text-gray-700">Email</Label>
          <Input
            id="email"
            type="email"
            value={invitation.email || ''}
            disabled
            readOnly
            className="h-11 bg-gray-100 text-gray-500 cursor-not-allowed"
            data-testid="onboarding-email"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="firstName" className="text-sm text-gray-700">First name</Label>
            <Input
              id="firstName" name="firstName"
              value={form.firstName} onChange={handle}
              className={`h-11 ${errors.firstName ? 'border-red-500' : ''}`}
              data-testid="onboarding-first-name"
            />
            {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName" className="text-sm text-gray-700">Last name</Label>
            <Input
              id="lastName" name="lastName"
              value={form.lastName} onChange={handle}
              className={`h-11 ${errors.lastName ? 'border-red-500' : ''}`}
              data-testid="onboarding-last-name"
            />
            {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
          </div>
        </div>

        {/* New password */}
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-sm text-gray-700">New password</Label>
          <div className="relative">
            <Input
              id="password" name="password"
              type={showPw ? 'text' : 'password'}
              value={form.password} onChange={handle}
              placeholder="At least 8 characters"
              className={`h-11 pr-11 ${errors.password ? 'border-red-500' : ''}`}
              data-testid="onboarding-password"
            />
            <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600" tabIndex={-1}>
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
        </div>

        {/* Confirm password */}
        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword" className="text-sm text-gray-700">Confirm password</Label>
          <div className="relative">
            <Input
              id="confirmPassword" name="confirmPassword"
              type={showPw2 ? 'text' : 'password'}
              value={form.confirmPassword} onChange={handle}
              className={`h-11 pr-11 ${errors.confirmPassword ? 'border-red-500' : ''}`}
              data-testid="onboarding-confirm-password"
            />
            <button type="button" onClick={() => setShowPw2(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600" tabIndex={-1}>
              {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl shadow-sm"
          data-testid="onboarding-submit"
        >
          {submitting ? 'Creating account…' : 'Set password & continue'}
        </Button>
      </form>

      <p className="text-xs text-gray-400 mt-6 text-center">
        By creating an account you agree to JobastiIQ&apos;s Terms of Service.
      </p>
    </AuthLayout>
  );
};

export default Onboarding;
